import { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { ShieldAlert, ChevronDown, ChevronUp, Check, AlertTriangle, Minus, Info, ExternalLink, BookOpen } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import AbbrTip from '@/components/AbbrTip';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GUIDE = '/learn/accounting-anomalies-enron-checklist';
const ITEM_NAMES = { 1: 'Income statement', 2: 'Balance sheet', 3: 'Value creation', 4: 'Financial distress', 5: 'Related parties', 6: 'Auditor fees', 7: 'M&A', 8: 'Jurisdiction', 9: 'Insiders & auditors', 10: 'Complexity' };
const pct = (v, d = 1) => (v == null ? '—' : `${(v * 100).toFixed(d)}%`);
const bn = (v) => (v == null ? '—' : Math.abs(v) >= 1e9 ? `$${(v / 1e9).toFixed(1)}B` : `$${(v / 1e6).toFixed(0)}M`);

function StatusIcon({ s }) {
  if (s === 'flag') return <AlertTriangle className="w-3.5 h-3.5 text-rose-500 shrink-0" />;
  if (s === 'na') return <Minus className="w-3.5 h-3.5 text-muted-foreground shrink-0" />;
  if (s === 'note') return <Info className="w-3.5 h-3.5 text-amber-500 shrink-0" />;
  return <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0" />;
}

function Explain({ open, onClose, title, children }) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto" data-testid="anomaly-explain-dialog">
        <DialogHeader>
          <DialogTitle className="text-sm pr-6">{title}</DialogTitle>
          <DialogDescription asChild><div className="space-y-2 text-xs text-foreground/85">{children}</div></DialogDescription>
        </DialogHeader>
      </DialogContent>
    </Dialog>
  );
}

function DetailText({ text }) {
  const parts = String(text).split(/(https?:\/\/\S+)/g);
  return parts.map((p, i) => (/^https?:\/\//.test(p)
    ? <a key={i} href={p} target="_blank" rel="noopener noreferrer" className="text-sky-600 dark:text-sky-400 underline" data-testid="anomaly-detail-link">open on EDGAR</a>
    : <span key={i}>{p}</span>));
}

function FlagRow({ f }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className="w-full flex items-start gap-2 text-left px-2 py-1.5 rounded hover:bg-muted/50 transition-colors" data-testid={`anomaly-flag-${f.id}`} data-status={f.status}>
        <StatusIcon s={f.status} />
        <span className="min-w-0 flex-1">
          <span className={`block text-xs font-semibold ${f.status === 'flag' ? 'text-rose-600 dark:text-rose-300' : f.status === 'note' ? 'text-amber-600 dark:text-amber-300' : f.status === 'na' ? 'text-muted-foreground' : 'text-foreground'}`}>{f.label}</span>
          <span className="block text-[11px] text-muted-foreground font-mono truncate">{f.value || f.threshold}</span>
        </span>
      </button>
      <Explain open={open} onClose={() => setOpen(false)} title={`Item ${f.item} · ${f.label}`}>
        {f.value && <p><strong>Measured:</strong> <span className="font-mono">{f.value}</span></p>}
        <p><strong>Flag when:</strong> {f.threshold}</p>
        {f.formula && <p><strong>Formula:</strong> <span className="font-mono">{f.formula}</span></p>}
        {f.detail && <p className="break-words"><strong>Filings:</strong> <DetailText text={f.detail} /></p>}
        {f.benign && <p className="text-muted-foreground"><strong>What would make this benign:</strong> {f.benign}</p>}
      </Explain>
    </>
  );
}

function ScorePill({ label, abbr, value, alarm, explain, testId }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className={`rounded-md border px-2 py-1.5 text-left hover:bg-muted/50 transition-colors ${alarm ? 'border-rose-500/50 bg-rose-500/5' : 'border-border/60'}`} data-testid={testId}>
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground"><AbbrTip term={abbr}>{label}</AbbrTip></div>
        <div className={`font-mono text-sm font-bold ${alarm ? 'text-rose-600 dark:text-rose-300' : 'text-foreground'}`}>{value}</div>
      </button>
      <Explain open={open} onClose={() => setOpen(false)} title={label}>{explain}</Explain>
    </>
  );
}

const COLS = [
  ['revenue', 'Revenue', bn], ['rev_growth', 'Rev growth', pct], ['gross_margin', 'Gross margin', pct], ['net_margin', 'Net margin', pct],
  ['roa', 'ROA', pct], ['asset_turnover', 'Asset turnover', (v) => (v == null ? '—' : v.toFixed(2))], ['debt_ratio', 'Liabilities / assets', pct],
  ['recv_to_rev', 'Receivables / revenue', pct], ['dsri', 'DSRI', (v) => (v == null ? '—' : v.toFixed(2))], ['accruals', '(NI − CFO) / assets', pct],
  ['fcf', 'Free cash flow', bn], ['soft_assets', 'Goodwill + intangibles', pct], ['roic_minus_wacc', 'ROIC − WACC', pct], ['c_score', 'C-Score', (v) => (v == null ? '—' : String(v))],
];

export default function AccountingAnomaliesCard({ ticker }) {
  const [d, setD] = useState(null);
  const [showTable, setShowTable] = useState(false);
  const [showVerdict, setShowVerdict] = useState(false);
  useEffect(() => {
    let alive = true; setD(null);
    if (!ticker) return undefined;
    axios.get(`${API}/stocks/${encodeURIComponent(ticker)}/accounting-anomalies`, { timeout: 120000 }).then((r) => alive && setD(r.data)).catch(() => alive && setD({ has_data: false }));
    return () => { alive = false; };
  }, [ticker]);
  if (d === null) return <div className="p-3 text-xs text-muted-foreground" data-testid="anomalies-loading">Running the year-over-year accounting checklist…</div>;
  if (!d.has_data) return null;
  const s = d.summary; const sc = d.scores; const yrs = d.table.slice(-8);
  const tone = s.n_flagged === 0 ? 'text-emerald-600 dark:text-emerald-400' : s.n_flagged <= 3 ? 'text-amber-600 dark:text-amber-400' : 'text-rose-600 dark:text-rose-400';
  return (
    <div className="rounded-lg border border-border/60 bg-card p-3 space-y-3" data-testid="accounting-anomalies-card">
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <ShieldAlert className="w-4 h-4 text-fuchsia-500" />
          <span className="text-xs font-bold uppercase tracking-wider text-fuchsia-600 dark:text-fuchsia-300">Accounting Anomalies</span>
          <span className="text-[10px] text-muted-foreground">{d.years[0]}–{d.years[d.years.length - 1]} · {d.years_count} yrs</span>
        </div>
        <button type="button" onClick={() => setShowVerdict(true)} className={`text-xs font-bold ${tone} hover:underline text-left`} data-testid="anomalies-verdict">
          {s.n_flagged} of {s.n_computed} items flagged · {s.verdict}
        </button>
      </div>
      <p className="text-[11px] font-bold text-blue-600 dark:text-blue-400" data-testid="anomalies-tap-hint">
        Click or tap anything on this page for definitions or explanations.
      </p>
      <Explain open={showVerdict} onClose={() => setShowVerdict(false)} title="How this score works">
        <p>The Polytope Fraud Theory checklist has ten items. DiviDrip computes {s.n_computed} for this company ({s.items_computed.map((i) => ITEM_NAMES[i]).join(', ')}) from the filed statements, 8-Ks{s.items_computed.includes(10) || s.items_computed.includes(6) ? ', the 10-K Exhibit 21 (EX-21) subsidiary list and the DEF 14A proxy (auditor fee table, auditor tenure, Related Person Transactions section)' : ''}; anything left over needs a read of the filings, linked below. An item counts as flagged when any of its checks trips.</p>
        <p>The paper&rsquo;s rule: more than 3 items &rarr; caution, more than 7 &rarr; avoid. Scaled here: 0 clean · 1–3 look closer · 4–5 multiple red flags · 6+ avoid until explained.</p>
        {s.exclusions && <p><strong>Note:</strong> {s.exclusions}</p>}
        {!s.trend_ready && <p><strong>Only {d.years_count} years filed</strong> — trend checks (DSRI, C-Score, accruals) need 3+.</p>}
        <p className="text-muted-foreground">Anomalies are questions, not verdicts. Enron tripped all ten; most companies with one or two have a boring explanation. Tap any row for the formula and the benign case.</p>
      </Explain>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <ScorePill label="Beneish M" abbr="BENEISH_M" value={sc.beneish_m == null ? '—' : sc.beneish_m.toFixed(2)} alarm={sc.beneish_alarm} testId="anomaly-score-m"
          explain={<><p>8-variable earnings-manipulation score. <strong>Alarm above −1.78</strong> (the paper&rsquo;s line; Beneish&rsquo;s original was −1.78 to −2.22).</p><p>Measured: <span className="font-mono">{sc.beneish_m?.toFixed(2) ?? '—'}</span>. Uses net PPE where gross is not published.</p></>} />
        <ScorePill label="C-Score" abbr="C_SCORE" value={sc.c_score == null ? '—' : `${sc.c_score} / 6`} alarm={sc.c_alarm} testId="anomaly-score-c"
          explain={<><p>Montier&rsquo;s six yes/no manipulation levers. <strong>Alarm at 4 or more.</strong></p><p>Counts: net income outrunning cash flow, receivable days up, inventory days up, other current assets up, depreciation slowing, assets +10%.</p></>} />
        <ScorePill label="Sloan accruals" abbr="SLOAN_ACCRUALS" value={pct(sc.sloan)} alarm={sc.sloan_alarm} testId="anomaly-score-sloan"
          explain={<><p>(Net income − Operating cash flow) ÷ average assets. <strong>Alarm beyond ±10%.</strong></p><p>Positive = profits not yet cash. One year can be a tax deposit or a big year-end receivable; persistence is the problem.</p></>} />
        <ScorePill label="ROIC − WACC" abbr="ROIC" value={pct(sc.roic_minus_wacc)} alarm={sc.roic_minus_wacc != null && sc.roic_minus_wacc < 0} testId="anomaly-score-eva"
          explain={<><p>Economic value added. <strong>Alarm below 0</strong> — the business earns less on its capital than the capital costs.</p><p>WACC is approximate: 12% cost of equity (the paper&rsquo;s conservative figure) and interest expense ÷ debt after tax.</p></>} />
      </div>
      <div className="divide-y divide-border/40" data-testid="anomaly-flags">
        {d.flags.map((f) => <FlagRow key={f.id} f={f} />)}
      </div>
      {d.manual.length > 0 && <div className="space-y-1" data-testid="anomaly-manual">
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground px-2">Check yourself (not in structured data)</div>
        {d.manual.map((m) => (
          <a key={m.item} href={m.url} target="_blank" rel="noopener noreferrer" className="flex items-start gap-2 px-2 py-1.5 rounded hover:bg-muted/50 transition-colors" data-testid={`anomaly-manual-${m.item}`}>
            <ExternalLink className="w-3.5 h-3.5 text-sky-500 shrink-0 mt-0.5" />
            <span className="min-w-0"><span className="block text-xs font-semibold">Item {m.item} · {m.label}</span><span className="block text-[11px] text-muted-foreground">{m.hint}</span></span>
          </a>
        ))}
      </div>}
      <button type="button" onClick={() => setShowTable((v) => !v)} className="inline-flex items-center gap-1 text-xs font-semibold text-fuchsia-600 dark:text-fuchsia-300 hover:underline px-2" data-testid="anomaly-table-toggle">
        {showTable ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />} {showTable ? 'Hide' : 'Show'} the year-over-year table ({yrs.length} yrs)
      </button>
      {showTable && (
        <div className="overflow-x-auto -mx-1" data-testid="anomaly-table">
          <table className="text-[11px] font-mono min-w-full">
            <thead><tr className="text-muted-foreground"><th className="text-left px-2 py-1 font-sans">Metric</th>{yrs.map((r) => <th key={r.year} className="text-right px-2 py-1">{r.year}</th>)}</tr></thead>
            <tbody>
              {COLS.map(([k, label, fmt], i) => (
                <tr key={k} className={i % 2 ? 'bg-sky-100/60 dark:bg-sky-950/25' : 'bg-emerald-100/60 dark:bg-emerald-950/25'}>
                  <td className="px-2 py-1 font-sans font-semibold whitespace-nowrap">{label}</td>
                  {yrs.map((r) => <td key={r.year} className="text-right px-2 py-1 tabular-nums">{fmt(r[k])}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="flex flex-wrap gap-x-4 gap-y-1">
        <Link to={GUIDE} className="inline-flex items-center gap-1 text-[11px] font-semibold text-primary hover:underline px-2" data-testid="anomaly-guide-link">
          <BookOpen className="w-3 h-3" /> Reading a Company Like a Short-Seller — the Enron Checklist
        </Link>
        <Link to="/accounting-anomalies" className="inline-flex items-center gap-1 text-[11px] font-semibold text-fuchsia-600 dark:text-fuchsia-300 hover:underline px-2" data-testid="anomaly-hub-link">
          <ShieldAlert className="w-3 h-3" /> Cleanest &amp; most-flagged payers by sector
        </Link>
      </div>
    </div>
  );
}
